/**
 * What a hotbar slot shows for its cooldown (DJ, 2026-10-07: "make our hotbar
 * cooldowns more clear; add a text timer on it, or at least as an option").
 *
 * Pure: ActionBarSlot reads CooldownStore and passes the numbers in, the
 * overlay draws the result. Four states, in priority order:
 *   - `recast`: a recast chain is open (Harebound's 2nd/3rd bound). The ability
 *     can be pressed again, so this is NOT a cooldown: no sweep, no number, no
 *     dim; the slot shows the bounds left and the window draining.
 *   - `cooldown`: the ability's own cooldown outlasts the global cooldown. Sweep,
 *     dim and (if the player keeps the setting on) a seconds countdown.
 *   - `gcd`: only the half-second global cooldown. A light sweep, no number —
 *     a number flickering on every slot after every cast is noise.
 *   - `ready`.
 */

export type SlotCooldownMode = 'ready' | 'gcd' | 'cooldown' | 'recast';

export interface SlotCooldownState {
  mode: SlotCooldownMode;
  /** Time until the state ends, ms (0 when ready). */
  remainingMs: number;
  /** Share of the state still to run, 0..1 (the sweep's dark part). */
  fraction: number;
  /** Countdown text for `cooldown`, else null. */
  label: string | null;
  /** Presses left in an open recast chain (`recast` only). */
  castsLeft: number;
  /** Charges available, if this is a charge-based ability. */
  charges?: number;
  maxCharges?: number;
}

export interface SlotCooldownInput {
  now: number;
  /** CooldownStore expiry for the ability (epoch ms; 0 = none). */
  cooldownExpiresAt: number;
  /** The ability's cooldown, ms (registry metadata is in ms). */
  cooldownMs: number;
  /** CooldownStore GCD start (epoch ms; 0 = none). */
  gcdStartedAt: number;
  gcdMs: number;
  /** Open recast window, if any (CooldownStore.getRecastWindow). */
  recast?: { until: number; castsLeft: number; windowMs: number } | null;
  /** Ability charge state (from CooldownStore.getCharges), if any. */
  charges?: {
    currentCharges: number;
    maxCharges: number;
    rechargeTimeMs: number;
    rechargeStartedAt: number | null;
  } | null;
}

/** Below this many ms the countdown shows tenths. */
export const COUNTDOWN_TENTHS_BELOW_MS = 3000;

const READY: SlotCooldownState = { mode: 'ready', remainingMs: 0, fraction: 0, label: null, castsLeft: 0 };

/**
 * Countdown text: whole seconds rounded up ("12", "4"), tenths at 3 s and
 * below ("3.0", "2.4", "0.1"), minutes past 90 s ("2m"). Never shows "0".
 */
export function formatCooldown(ms: number): string {
  if (ms <= 0) return '';
  if (ms <= COUNTDOWN_TENTHS_BELOW_MS) return (Math.ceil(ms / 100) / 10).toFixed(1);
  if (ms > 90_000) return `${Math.ceil(ms / 60_000)}m`;
  return String(Math.ceil(ms / 1000));
}

function share(remaining: number, total: number): number {
  if (total <= 0) return 1;
  return Math.max(0, Math.min(1, remaining / total));
}

export function slotCooldownState(input: SlotCooldownInput): SlotCooldownState {
  const { now, recast, charges } = input;
  if (charges) {
    const { currentCharges, maxCharges, rechargeTimeMs, rechargeStartedAt } = charges;
    const isRecharging = currentCharges < maxCharges && rechargeStartedAt !== null;
    let remainingMs = 0;
    if (isRecharging) {
      const elapsed = Math.max(0, now - rechargeStartedAt);
      remainingMs = Math.max(0, rechargeTimeMs - (elapsed % rechargeTimeMs));
    }
    const fraction = isRecharging ? share(remainingMs, rechargeTimeMs) : 0;

    if (currentCharges <= 0) {
      return {
        mode: 'cooldown',
        remainingMs,
        fraction,
        label: formatCooldown(remainingMs),
        castsLeft: 0,
        charges: currentCharges,
        maxCharges,
      };
    }

    return {
      mode: 'ready',
      remainingMs,
      fraction,
      label: null,
      castsLeft: 0,
      charges: currentCharges,
      maxCharges,
    };
  }

  if (recast && recast.castsLeft > 0 && recast.until > now) {
    const remainingMs = recast.until - now;
    return { mode: 'recast', remainingMs, fraction: share(remainingMs, recast.windowMs), label: null, castsLeft: recast.castsLeft };
  }
  const cooldownRemaining = Math.max(0, input.cooldownExpiresAt - now);
  const gcdRemaining = input.gcdStartedAt > 0 ? Math.max(0, input.gcdMs - (now - input.gcdStartedAt)) : 0;
  if (cooldownRemaining > 0 && cooldownRemaining >= gcdRemaining) {
    return {
      mode: 'cooldown',
      remainingMs: cooldownRemaining,
      fraction: share(cooldownRemaining, input.cooldownMs),
      label: formatCooldown(cooldownRemaining),
      castsLeft: 0,
    };
  }
  if (gcdRemaining > 0) {
    return { mode: 'gcd', remainingMs: gcdRemaining, fraction: share(gcdRemaining, input.gcdMs), label: null, castsLeft: 0 };
  }
  return READY;
}
